import React from 'react';
import RegionalDancersSVG from './RegionalDancersSVG';

const FoodThemeProvider = ({ children, enableDancers = true }) => {
    // Enable decorative overlays globally (neutral, context-aware)
    const ENABLE_DECOR = true;
    // Neutral decorative tokens (only food/abstract)
    const foodProps = {
        header: ['🍛', '🥘', '🍲', '🥗'],
        sidebar: ['🧄', '🧅', '🍅', '🥕', '🥒', '🥦', '🍆', '🌿', '🍃', '🌶️'],
        content: ['🥗', '🍲', '🥙', '🍜', '🍛', '🥘'],
        footer: ['🍯', '🥛', '🍵', '☕', '🥤', '🧊', '🍶'],
        cards: ['🍕', '🍔', '🌭', '🥪', '🍛', '🥘', '🍲', '🥙', '🧁', '🍪'],
        buttons: ['🌿', '🍃', '🥜', '🥚','🧄', '🧅', '🍅'],
        forms: ['🧀', '🥓', '🍖', '🍗', '🥩', '🦐', '🐟', '🍤', '🍯', '🍵'],
        navigation: ['🦐', '🐟', '🍤', '🥩', '🍖', '🍗', '🥓', '🧀'],
        indian: ['🍛','🥘','🍲','🥙','🍜','🍝','🍢','🍡','🥟','🍚','🌿','🍃','🌶️']
    };

    // Context-aware, neutral emoji mapping
    const contextEmojiMap = {
        // Food categories
        dessert: ['🍨','🍧','🍰','🧁','🍪','🍩','🍫','🍭','🍮','🥧'],
        sweets: ['🍰','🧁','🍬','🍫','🍯','🍡'],
        beverage: ['🥤','🧃','🧋','🍵','☕','🥛','🧊'],
        bakery: ['🥐','🥖','🥨','🥯','🍞','🧁','🍪','🥧'],
        seafood: ['🐟','🍤','🦐','🦑','🦀','🦞'],
        spicy: ['🌶️','🔥','🥵'],
        vegetarian: ['🥬','🥦','🥒','🍅','🥕','🌿'],
        vegan: ['🌱','🥬','🥑','🥦'],
        protein: ['🍗','🥩','🍖','🥓','🧀'],
        noodles: ['🍜','🍝','🍲'],
        rice: ['🍚','🍙','🍛'],
        snacks: ['🍟','🌮','🌯','🍔','🍕','🥪'],
        // Non-food sections
        photography: ['📷','📸','🎞️','🖼️','📹'],
        logistics: ['🚚','📦','🧭','⏱️'],
        contact: ['☎️','✉️','📨','📱'],
        booking: ['🗓️','🕒','📝'],
        pricing: ['💲','💵','🧾'],
        rating: ['⭐','✨','🌟'],
        default: ['🍛','🥘','🍲','🥗']
    };

    const getContextEmoji = (el, fallbackCategory = 'content') => {
        try {
            // 1) Explicit data attribute on the element
            const attr = el?.dataset?.emojiContext;
            if (attr && contextEmojiMap[attr]) {
                const set = contextEmojiMap[attr];
                return set[Math.floor(Math.random() * set.length)];
            }

            // 2) Inherit from closest ancestor with data-emoji-context
            const parentWithAttr = el?.closest?.('[data-emoji-context]');
            const parentAttr = parentWithAttr?.dataset?.emojiContext;
            if (parentAttr && contextEmojiMap[parentAttr]) {
                const set = contextEmojiMap[parentAttr];
                return set[Math.floor(Math.random() * set.length)];
            }

            // 3) Infer from text content/class names (basic keyword heuristic)
            const text = (el?.innerText || el?.textContent || '').toLowerCase();
            const cls = (el?.className || '').toString().toLowerCase();
            const hay = text + ' ' + cls;

            const tests = [
                ['photography', /(photo|gallery|camera|shoot|image|media)/],
                ['dessert', /(dessert|sweet|mithai|cake|pastry|gulab|rasg|laddu)/],
                ['beverage', /(drink|juice|beverage|tea|coffee|shake|soda)/],
                ['seafood', /(fish|prawn|shrimp|sea ?food|crab|lobster|squid)/],
                ['spicy', /(spicy|chili|masala|pepper|tandoori|peri|schezwan)/],
                ['vegetarian', /(veg|vegetarian|paneer|salad|greens)/],
                ['vegan', /(vegan|plant ?based)/],
                ['bakery', /(bakery|bread|bake|bun|roll|puff|croissant|bagel|pastry)/],
                ['noodles', /(noodle|pasta|spaghetti|ramen|soba|udon)/],
                ['rice', /(rice|biryani|fried rice|pulao|khichdi)/],
                ['snacks', /(snack|starter|chaat|fries|sandwich|pizza|burger|wrap|taco|roll)/],
                ['contact', /(contact|email|phone|reach|support|help)/],
                ['booking', /(book|booking|schedule|reserve|reservation|calendar)/],
                ['pricing', /(price|pricing|quote|estimate|bill|invoice)/],
                ['rating', /(rating|review|star)/],
            ];

            for (const [ctx, regex] of tests) {
                if (regex.test(hay)) {
                    const set = contextEmojiMap[ctx] || contextEmojiMap.default;
                    return set[Math.floor(Math.random() * set.length)];
                }
            }

            // 4) Fallback to section category set or default
            const set = foodProps[fallbackCategory] || contextEmojiMap.default;
            return set[Math.floor(Math.random() * set.length)];
        } catch (_) {
            const set = contextEmojiMap.default;
            return set[Math.floor(Math.random() * set.length)];
        }
    };

    // Regional SVG motifs (subtle background art)
    const regionalThemes = {
        punjab: {
            name: 'Punjab',
            svgs: [
                (key) => (
                    <svg key={`pb-phulkari-${key}`} className="regional-svg" viewBox="0 0 200 200">
                        <defs>
                            <linearGradient id={`pb-g-${key}`} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#f59e0b" />
                                <stop offset="100%" stopColor="#ea580c" />
                            </linearGradient>
                        </defs>
                        <g fill="none" stroke={`url(#pb-g-${key})`} strokeWidth="1.2">
                            {Array.from({ length: 12 }, (_, i) => (
                                <polygon key={i} points="100,10 120,60 100,110 80,60" transform={`rotate(${i * 30} 100 60)`} />
                            ))}
                        </g>
                    </svg>
                )
            ]
        },
        gujarat: {
            name: 'Gujarat',
            svgs: [
                (key) => (
                    <svg key={`gj-bandhani-${key}`} className="regional-svg" viewBox="0 0 200 200">
                        <g fill="none" stroke="#16a34a" strokeOpacity="0.6" strokeWidth="0.8">
                            {Array.from({ length: 8 }, (_, r) => (
                                <circle key={r} cx="100" cy="100" r={(r + 1) * 12} />
                            ))}
                            {Array.from({ length: 24 }, (_, i) => (
                                <circle key={i} cx={100 + Math.cos((i / 24) * Math.PI * 2) * 60} cy={100 + Math.sin((i / 24) * Math.PI * 2) * 60} r="2" />
                            ))}
                        </g>
                    </svg>
                )
            ]
        },
        bengal: {
            name: 'West Bengal',
            svgs: [
                (key) => (
                    <svg key={`wb-alpona-${key}`} className="regional-svg" viewBox="0 0 200 200">
                        <g fill="none" stroke="#0ea5e9" strokeOpacity="0.55" strokeWidth="0.9">
                            {Array.from({ length: 6 }, (_, i) => (
                                <path key={i} d={`M20 ${30 + i * 20} C 80 ${10 + i * 15}, 120 ${50 + i * 10}, 180 ${30 + i * 20}`} />
                            ))}
                        </g>
                    </svg>
                )
            ]
        },
        tamil: {
            name: 'Tamil Nadu',
            svgs: [
                (key) => (
                    <svg key={`tn-kolam-${key}`} className="regional-svg" viewBox="0 0 200 200">
                        <g fill="none" stroke="#eab308" strokeOpacity="0.6" strokeWidth="0.8">
                            {Array.from({ length: 5 }, (_, i) => (
                                <rect key={i} x={40 - i * 8} y={40 - i * 8} width={120 + i * 16} height={120 + i * 16} rx="12" ry="12" transform="rotate(45 100 100)" />
                            ))}
                        </g>
                    </svg>
                )
            ]
        },
        kerala: {
            name: 'Kerala',
            svgs: [
                (key) => (
                    <svg key={`kl-theyyam-${key}`} className="regional-svg" viewBox="0 0 200 200">
                        <g fill="none" stroke="#dc2626" strokeOpacity="0.55" strokeWidth="1">
                            {Array.from({ length: 12 }, (_, i) => (
                                <path key={i} d={`M100 20 L ${50 + i * 8} 180 L ${150 - i * 8} 180 Z`} />
                            ))}
                        </g>
                    </svg>
                )
            ]
        },
        rajasthan: {
            name: 'Rajasthan',
            svgs: [
                (key) => (
                    <svg key={`rj-jaali-${key}`} className="regional-svg" viewBox="0 0 200 200">
                        <defs>
                            <pattern id={`rj-p-${key}`} width="20" height="20" patternUnits="userSpaceOnUse">
                                <circle cx="10" cy="10" r="2.5" fill="#a16207" fillOpacity="0.5" />
                                <rect x="0" y="0" width="20" height="20" fill="none" stroke="#a16207" strokeOpacity="0.35" strokeWidth="0.6" />
                            </pattern>
                        </defs>
                        <rect x="20" y="20" width="160" height="160" fill={`url(#rj-p-${key})`} />
                    </svg>
                )
            ]
        }
    };

    const regionalThemeKeys = Object.keys(regionalThemes);
    const selectedRegionRef = React.useRef(null);
    if (selectedRegionRef.current === null) {
        selectedRegionRef.current = regionalThemeKeys[Math.floor(Math.random() * regionalThemeKeys.length)];
    }
    const selectedRegion = selectedRegionRef.current;

    const regionalProps = {
        punjab: ['🍛','🫓','🥛','🧈','🌿','🥗','🥙','🥁','🕺','🧣','🎶','🌾'],
        gujarat: ['🫓','🥗','🍘','🫘','🌶️','🥜','🍵','🪘','👘','🐫','💃','🌿'],
        bengal: ['🐟','🍚','🍤','🥭','🍯','🍵','🦚','🌿','🎭','🪭','🚜','🐄'],
        tamil: ['🍚','🍛','🍲','🫓','🫚','🫛','🌿','🐘','🎶','💃','👗'],
        kerala: ['🐟','🍤','🥥','🍚','🌿','🫓','🐘','🚣‍♂️','🎭','🌴','🏝'],
        maharashtra: ['🍛','🫓','🌶️','🥜','🍵','🥭','🥁','🌿','👘','🎊','🎭'],
        rajasthan: ['🫓','🧈','🍛','🌶️','🐪','🌿','🏜️','🎭','🪘','🧣'],
        goa: ['🐟','🍤','🍛','🍹','🌴','🌶️','🌿','🪸','⛱️','🎶','💃','🚤'],
        andhra: ['🌶️','🍛','🍚','🐟','🍤','🫚','🫛','🌿','🎉'],
        kashmir: ['🍛','🍖','🥘','🍚','🧄','🧅','❄️','🌿','🏔️','🧣','🎿','🕊️'],
        assam: ['🍚','🫓','🍵','🌾','🌿','🪶','🧵','🧑‍🌾','🎶','🐦','🌸'],
        odisha: ['🍚','🍛','🍵','🌿','🧵','🎭','🐂','🎇'],
        manipur: ['🍚','🌿','🎭','🐎','🥁','🧣','🪶','🎶','🦎','🧵'],
        meghalaya: ['🍚','🌿','🎶','🐘','🏞️','🧵','🪶','☔','🎭'],
        tripura: ['🍲','🌿','🌸','🎭','🪞','💃','🐂','🧵'],
        jharkhand: ['🍚','🌿','🎶','🧵','🏞️','🪘','🦌','🎭'],
        uttarPradesh: ['🍛','🍚','🌿','🎭','📿','👜','🧵','🏰'],
        haryana: ['🍚','🌿','🎶','🚜','🏠','🧵','🧶','🪕'],
        himachal: ['🥘','🍚','❄️','🌲','🧣','🧤','🏔️','🪶'],
        sikkim: ['🥘','🍚','🌿','⛰️','🧣','🎶','🐐','🪶','🧵'],
        telangana: ['🍚','🍛','🥥','🌿','🎉','🧵','🪶','🚜','🎶']
      };
      

    const regionalCategories = Object.keys(regionalProps);

    // Random food prop generator
    const getRandomFoodProp = (category = 'content') => {
        const props = foodProps[category] || foodProps.content;
        return props[Math.floor(Math.random() * props.length)];
    };

    const getRandomRegionalProp = () => {
        const region = regionalCategories[Math.floor(Math.random() * regionalCategories.length)];
        const props = regionalProps[region];
        return props[Math.floor(Math.random() * props.length)];
    };

    // Add food props to DOM elements
    React.useEffect(() => {
        if (!ENABLE_DECOR) return;
        const addFoodProps = () => {
            // Add props to headers
            document.querySelectorAll('h1, h2, h3').forEach((header, index) => {
                if (!header.dataset.foodPropAdded) {
                    const prop = getContextEmoji(header, 'header');
                    header.style.position = 'relative';
                    header.style.paddingLeft = '30px';
                    header.dataset.foodPropAdded = 'true';
                    
                    const propElement = document.createElement('span');
                    propElement.textContent = prop;
                    propElement.style.position = 'absolute';
                    propElement.style.left = '0';
                    propElement.style.top = '50%';
                    propElement.style.transform = 'translateY(-50%)';
                    propElement.style.fontSize = '1.2rem';
                    propElement.style.opacity = '0.7';
                    propElement.style.animation = 'pulse 2s ease-in-out infinite';
                    propElement.style.animationDelay = `${index * 0.5}s`;
                    
                    header.appendChild(propElement);
                }
            });

            // Add props to cards - only if a context is present or can be inferred
            document.querySelectorAll('.ant-card, .card').forEach((card, index) => {
                if (!card.dataset.foodPropAdded) {
                    const prop = getContextEmoji(card, 'cards');
                    card.style.position = 'relative';
                    card.dataset.foodPropAdded = 'true';
                    
                    const propElement = document.createElement('div');
                    propElement.textContent = prop;
                    propElement.style.position = 'absolute';
                    propElement.style.top = '10px';
                    propElement.style.right = '10px';
                    propElement.style.fontSize = '1.5rem';
                    propElement.style.opacity = '0.22';
                    propElement.style.animation = 'float 3s ease-in-out infinite';
                    propElement.style.animationDelay = `${index * 0.3}s`;
                    propElement.style.pointerEvents = 'none';
                    
                    card.appendChild(propElement);
                }
            });

            // Skip adding props to buttons to reduce clutter

            // Add props to form inputs
            document.querySelectorAll('.ant-input, input, select').forEach((input, index) => {
                if (!input.dataset.foodPropAdded) {
                    const prop = getContextEmoji(input, 'forms');
                    input.dataset.foodPropAdded = 'true';
                    const propElement = document.createElement('span');
                    propElement.textContent = prop;
                    propElement.style.position = 'absolute';
                    propElement.style.right = '-30px';
                    propElement.style.top = '50%';
                    propElement.style.transform = 'translateY(-50%)';
                    propElement.style.fontSize = '1rem';
                    propElement.style.opacity = '0.4';
                    propElement.style.animation = 'pulse 3s ease-in-out infinite';
                    propElement.style.animationDelay = `${index * 0.4}s`;
                    propElement.style.pointerEvents = 'none';
                    propElement.style.zIndex = '10';
                    
                    input.parentElement.appendChild(propElement);
                }
            });

            // Add props to footers
            document.querySelectorAll('footer, .site-footer, .ant-layout-footer').forEach((footer, index) => {
                if (!footer.dataset.foodPropAdded) {
                    const prop = getContextEmoji(footer, 'footer');
                    footer.style.position = footer.style.position || 'relative';
                    footer.dataset.foodPropAdded = 'true';

                    const propElement = document.createElement('div');
                    propElement.textContent = prop;
                    propElement.style.position = 'absolute';
                    propElement.style.left = index % 2 === 0 ? '12px' : 'auto';
                    propElement.style.right = index % 2 !== 0 ? '12px' : 'auto';
                    propElement.style.bottom = '12px';
                    propElement.style.fontSize = '1.4rem';
                    propElement.style.opacity = '0.35';
                    propElement.style.animation = 'sway 3.5s ease-in-out infinite';
                    propElement.style.pointerEvents = 'none';

                    footer.appendChild(propElement);
                }
            });
        };

        // Add props after a short delay to ensure DOM is ready
        const timer = setTimeout(addFoodProps, 100);
        // Add a global body class to enable compact cards styles site-wide
        try { document.body.classList.add('card-compact'); } catch (_) {}
        
        // Re-add props when route changes (for SPA)
        const observer = new MutationObserver(() => {
            setTimeout(addFoodProps, 100);
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => {
            clearTimeout(timer);
            observer.disconnect();
            try { document.body.classList.remove('card-compact'); } catch (_) {}
        };
    }, []);

    // Dancer visibility toggle: prop, localStorage, or body class
    const [showDancers, setShowDancers] = React.useState(true);
    React.useEffect(() => {
        try {
            const ls = (typeof window !== 'undefined' && window.localStorage) ? localStorage.getItem('enableDancers') : null;
            const bodyHasHide = typeof document !== 'undefined' && document.body.classList.contains('hide-cultural-dancers');
            const lsEnabled = ls === null ? true : ls !== 'false';
            setShowDancers(enableDancers && lsEnabled && !bodyHasHide);
        } catch (_) {
            setShowDancers(enableDancers);
        }
    }, [enableDancers]);

    // Force-enable dancers when requested
    React.useEffect(() => {
        if (!enableDancers) return;
        try {
            if (typeof document !== 'undefined') {
                document.body.classList.remove('hide-cultural-dancers');
            }
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.setItem('enableDancers', 'true');
            }
            setShowDancers(true);
        } catch (_) {}
    }, [enableDancers]);

    return (
        <div className="food-theme-container">
            {children}
            {ENABLE_DECOR && (
            <div className="regional-theme">
                {regionalThemes[selectedRegion].svgs.map((SvgFn, i) => (
                    <div
                        key={`regional-svg-${i}`}
                        className="regional-svg-wrapper"
                        style={{
                            position: 'fixed',
                            zIndex: -2,
                            left: `${10 + (i * 20) % 70}%`,
                            top: `${10 + (i * 30) % 70}%`,
                            width: `${180 + (i % 3) * 40}px`,
                            height: `${180 + (i % 3) * 40}px`,
                            pointerEvents: 'none',
                            opacity: 0.14,
                            transform: `rotate(${(i * 17) % 360}deg)`
                        }}
                    >
                        {SvgFn(i)}
                    </div>
                ))}
            </div>
            )}
            {ENABLE_DECOR && (
            <div className="floating-food-particles">
                {Array.from({ length: 20 }, (_, i) => {
                    const sizes = ['1rem', '1.2rem', '1.5rem', '1.8rem', '2rem', '2.2rem'];
                    const opacities = [0.08, 0.12, 0.15, 0.18, 0.22, 0.25, 0.28, 0.3];
                    const randomSize = sizes[Math.floor(Math.random() * sizes.length)];
                    const randomOpacity = opacities[Math.floor(Math.random() * opacities.length)];
                    
                    return (
                        <div
                            key={i}
                            className="food-particle"
                            style={{
                                position: 'fixed',
                                fontSize: randomSize,
                                opacity: randomOpacity,
                                pointerEvents: 'none',
                                zIndex: -1,
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animation: `float ${15 + Math.random() * 10}s ease-in-out infinite`,
                                animationDelay: `${Math.random() * 5}s`
                            }}
                        >
                            {getRandomRegionalProp()}
                        </div>
                    );
                })}
            </div>
            )}
            {ENABLE_DECOR && (
            <div className="indian-decorations">
                {Array.from({ length: 12 }, (_, i) => {
                    const sizes = ['1.5rem', '1.8rem', '2rem', '2.2rem', '2.5rem', '2.8rem'];
                    const opacities = [0.08, 0.1, 0.12, 0.15, 0.18, 0.2, 0.22, 0.25];
                    const randomSize = sizes[Math.floor(Math.random() * sizes.length)];
                    const randomOpacity = opacities[Math.floor(Math.random() * opacities.length)];
                    const decorativeEmojis = ['🌿', '🍃', '🍛', '🥘', '🍲', '🥙', '🍜', '🍝', '🍢', '🍡'];
                    
                    return (
                        <div
                            key={`decoration-${i}`}
                            className="indian-decoration"
                            style={{
                                position: 'fixed',
                                fontSize: randomSize,
                                opacity: randomOpacity,
                                pointerEvents: 'none',
                                zIndex: -1,
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animation: `indianFloat ${20 + Math.random() * 15}s ease-in-out infinite`,
                                animationDelay: `${Math.random() * 8}s`
                            }}
                        >
                            {decorativeEmojis[i % decorativeEmojis.length]}
                        </div>
                    );
                })}
            </div>
            )}
            {/* Disable dancers for neutrality */}
            {false && showDancers && (
                <div className="cultural-dancers">
                    <RegionalDancersSVG className="dancers-svg" />
                </div>
            )}
        </div>
    );
};

export default FoodThemeProvider;
